const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};

const isFrench = (tag: string): boolean => tag.toLowerCase().split('-')[0] === 'fr';

/**
 * Formats a history entry's date and time in the READER's locale — the same tag `react-intl` renders the
 * surrounding copy in (`useIntl().locale`), so the month words always match the language on screen.
 *
 * - One locale source: this used to receive the BUILDING locale while the copy followed the user's, so a
 *   French reader in a US building read French copy beside `April 24, 2024 at 5:46 PM`.
 * - The clock is the LOCALE's, not forced: `hour12: true` here used to give a German or French reader
 *   `3:05 PM`; en-US still reads `5:46 PM` because 12-hour is its own default.
 * - French writes the time with `h` (`17 h 46`) in every region — HqO's deliberate override of CLDR,
 *   which grants the `h` to fr-CA only and gives fr-FR a colon (the `@hqo/date-time` convention, applied
 *   here by language since the repo does not depend on that package).
 * - An underscore tag (`en_GB`) is hyphenated first — `Intl` rejects it with a RangeError — and the tag
 *   is passed as a LIST anchored to `en-US`, so a tag `Intl` does not know falls back to US English
 *   rather than to the device's locale.
 */
export const formatDate = (dateToFormat: string | undefined, locale: string): string | null => {
  if (!dateToFormat) {
    return null;
  }

  const date = new Date(dateToFormat);
  const tag = locale.replace(/_/g, '-');

  let format: Intl.DateTimeFormat;
  try {
    format = new Intl.DateTimeFormat([tag, 'en-US'], DATE_TIME_OPTIONS);
  } catch {
    format = new Intl.DateTimeFormat('en-US', DATE_TIME_OPTIONS);
  }

  if (!isFrench(format.resolvedOptions().locale)) {
    return format.format(date);
  }

  const parts = format.formatToParts(date);

  return parts
    .map((part, index) => (
      part.type === 'literal' && parts[index - 1]?.type === 'hour' && parts[index + 1]?.type === 'minute'
        ? ' h '
        : part.value
    ))
    .join('');
};
